import { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from "react"
import { useChatView } from "@/chat2/Chat2Provider"
import type { WorkspaceMember } from "@bloomstack/apis/workspace"
import { useService, useServiceEventListener } from "@/hooks/useService"
import type { ILLMServicePublic, ValidateAgentModelsResult } from "@bloomstack/apis/services/llmService"
import { LLM_SERVICE_IDENT } from "@bloomstack/apis/services/llmService"
import type { AgentUpdatedEvent } from "@bloomstack/apis/agentTypes"
import { RPC_EVENT_AGENT_UPDATED_IDENT } from "@bloomstack/apis/agentTypes"
import { RPC_EVENT_PROFILE_UPDATED_IDENT } from "@bloomstack/apis/modelTypes"

// ============================================================================
// TYPES
// ============================================================================

export type WorkspaceMembersContextType = {
  /** All workspace members (agents + humans) */
  members: WorkspaceMember[]
  /** Whether members are loading */
  isLoading: boolean
  /** Agent members only (displayType === "agent") */
  agents: WorkspaceMember[]
  /** Human members only (displayType === "user") */
  humans: WorkspaceMember[]
  /** Lookup a member by userProfileId */
  getMember: (userProfileId: string) => WorkspaceMember | undefined
  /**
   * LLM model validation results for agents.
   * null = validation not yet complete (treat as unknown / show normal state).
   */
  agentModelValidity: ValidateAgentModelsResult | null
  /**
   * Returns whether the agent's configured LLM model is valid.
   * - true  = valid
   * - false = invalid (show warning badge)
   * - null  = validation pending (show normal state)
   */
  isAgentModelValid: (userProfileId: string) => boolean | null
}

// ============================================================================
// CONTEXT
// ============================================================================

const WorkspaceMembersContext = createContext<WorkspaceMembersContextType | null>(null)

// ============================================================================
// PROVIDER
// ============================================================================

type WorkspaceMembersProviderProps = {
  children: ReactNode
}

/**
 * Provider for workspace members.
 *
 * Fetches and maintains a reactive list of workspace members.
 * Subscribes to memberAdded/memberRemoved events to stay in sync.
 *
 * Also performs a background LLM model validation for all agent members
 * after load, so the UI can flag agents with invalid/deprecated model IDs
 * before users attempt to trigger them.
 *
 * Must be nested inside ChatViewProvider (uses useChatView for workspaceId and events).
 */
export function WorkspaceMembersProvider({ children }: WorkspaceMembersProviderProps) {
  const { workspaceId, queryMembers, events } = useChatView()
  const llmService = useService<ILLMServicePublic>(LLM_SERVICE_IDENT)

  const [members, setMembers] = useState<WorkspaceMember[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [agentModelValidity, setAgentModelValidity] = useState<ValidateAgentModelsResult | null>(null)

  // ============================================================================
  // INITIAL FETCH
  // ============================================================================

  useEffect(() => {
    if (!workspaceId) {
      setMembers([])
      setIsLoading(false)
      return
    }

    const fetchMembers = async () => {
      setIsLoading(true)
      try {
        const result = await queryMembers({}, { limit: 1000 })
        if (result.success) {
          setMembers(result.result.results)
        }
      } catch (err) {
        console.error("[WorkspaceMembersProvider] Failed to fetch members:", err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMembers()
  }, [workspaceId, queryMembers])

  // ============================================================================
  // DERIVED DATA
  // ============================================================================

  const agents = useMemo(() =>
    members.filter(m => m.userProfile?.displayType === "agent"),
    [members]
  )

  // ============================================================================
  // AGENT MODEL VALIDATION
  // ============================================================================

  /**
   * Revalidate the LLM model for the given agent user profile IDs.
   * Used on initial load, membership changes, agent updates, and profile updates.
   */
  const revalidateAgentModels = useCallback(async (profileIds: string[]) => {
    try {
      const result = await llmService.validateAgentModels(profileIds)
      if (result.success) {
        return result.result
      }
    } catch (err) {
      // Non-critical — validation failure just means badges won't show.
      console.warn("[WorkspaceMembersProvider] Agent model validation failed:", err)
    }
    return null
  }, [llmService])

  // ============================================================================
  // EVENT SUBSCRIPTIONS
  // ============================================================================

  useEffect(() => {
    const handleMemberAdded = (member: WorkspaceMember) => {
      setMembers(prev => {
        // Avoid duplicates
        if (prev.some(m => m.userProfileId === member.userProfileId)) {
          return prev
        }
        return [...prev, member]
      })
    }

    const handleMemberRemoved = (_workspaceId: string, userProfileId: string) => {
      setMembers(prev => prev.filter(m => m.userProfileId !== userProfileId))
      // Also clear stale validity entry
      setAgentModelValidity(prev => {
        if (!prev || !(userProfileId in prev)) return prev
        const updated = { ...prev }
        delete updated[userProfileId]
        return updated
      })
    }

    events.subscribe("memberAdded", handleMemberAdded)
    events.subscribe("memberRemoved", handleMemberRemoved)

    return () => {
      events.unsubscribe("memberAdded", handleMemberAdded)
      events.unsubscribe("memberRemoved", handleMemberRemoved)
    }
  }, [events])

  // Re-fetch members when an agent profile is updated (e.g. avatar change)
  useServiceEventListener<AgentUpdatedEvent>(
    RPC_EVENT_AGENT_UPDATED_IDENT,
    async (agent) => {
      // Only re-fetch if the updated agent is a member of this workspace
      if (!members.some(m => m.userProfileId === agent.userProfileId)) return
      try {
        const result = await queryMembers({}, { limit: 1000 })
        if (result.success) {
          setMembers(result.result.results)
        }
      } catch (err) {
        console.error("[WorkspaceMembersProvider] Failed to refresh members after agent update:", err)
      }
      // Directly revalidate the affected agent's model (merge, don't blow away others)
      const fresh = await revalidateAgentModels([agent.userProfileId])
      if (fresh && fresh[agent.userProfileId]) {
        const entry = fresh[agent.userProfileId]
        setAgentModelValidity(prev => ({ ...(prev || {}), [agent.userProfileId]: entry }))
      }
    },
    [members, queryMembers, revalidateAgentModels]
  )

  // Revalidate all workspace agents when a model profile changes (e.g. deprecated flip),
  // since the profile broadcast may not be accompanied by any agent row change.
  useServiceEventListener(
    RPC_EVENT_PROFILE_UPDATED_IDENT,
    async () => {
      const profileIds = agents.map(a => a.userProfileId)
      if (profileIds.length === 0) return
      const fresh = await revalidateAgentModels(profileIds)
      if (fresh) {
        setAgentModelValidity(fresh)
      }
    },
    [agents, revalidateAgentModels]
  )

  // ============================================================================
  // DERIVED DATA
  // ============================================================================

  const humans = useMemo(() =>
    members.filter(m => m.userProfile?.displayType === "user"),
    [members]
  )

  const memberMap = useMemo(() =>
    new Map(members.map(m => [m.userProfileId, m])),
    [members]
  )

  const getMember = (userProfileId: string) => memberMap.get(userProfileId)

  // ============================================================================
  // BACKGROUND AGENT MODEL VALIDATION
  // ============================================================================

  useEffect(() => {
    if (agents.length === 0) {
      setAgentModelValidity(null)
      return
    }

    const validate = async () => {
      const profileIds = agents.map(a => a.userProfileId)
      const fresh = await revalidateAgentModels(profileIds)
      if (fresh) {
        setAgentModelValidity(fresh)
      }
    }

    validate()
  }, [agents, revalidateAgentModels])

  const isAgentModelValid = useCallback((userProfileId: string): boolean | null => {
    if (!agentModelValidity) return null
    const validation = agentModelValidity[userProfileId]
    if (validation === undefined) return null
    return validation.valid
  }, [agentModelValidity])

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: WorkspaceMembersContextType = useMemo(() => ({
    members,
    isLoading,
    agents,
    humans,
    getMember,
    agentModelValidity,
    isAgentModelValid,
  }), [members, isLoading, agents, humans, agentModelValidity, isAgentModelValid])

  return (
    <WorkspaceMembersContext.Provider value={value}>
      {children}
    </WorkspaceMembersContext.Provider>
  )
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access the ChatView-scoped workspace members context.
 *
 * This is the per-chat-tab provider hook. For the canonical, cached,
 * cross-component member roster use `useWorkspaceMembers` from
 * `@/hooks/useWorkspaceMembers` instead.
 *
 * @param throwIfMissing - If true (default), throws if used outside provider
 */
export function useChatViewMembers(throwIfMissing = true): WorkspaceMembersContextType | null {
  const context = useContext(WorkspaceMembersContext)

  if (throwIfMissing && !context) {
    throw new Error("useChatViewMembers must be used within a WorkspaceMembersProvider")
  }

  return context
}
